import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Search, Plus, Clapperboard, Upload, Trash2, AlertTriangle, Film, Tv } from "lucide-react";
import { api, API, formatApiError, fmtBytes } from "../lib/api";
import { GlassCard, PageHeader, QualityBadge, VersionBadge, Modal, EmptyState, Spinner } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";
import { useAuth } from "../context/AuthContext";

const QUALITIES = ["CAM", "TS", "SD", "HD", "FHD", "UHD4K"];
const VERSIONS = [
  { v: "dublado", label: "Dublado" },
  { v: "legendado", label: "Legendado" },
  { v: "dual", label: "Dual Áudio" },
  { v: "nacional", label: "Nacional" },
];

export default function Catalog() {
  const { user } = useAuth();
  const [titles, setTitles] = useState(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [tmdbError, setTmdbError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const debounce = useRef(null);

  // manual add
  const [manual, setManual] = useState({ tmdb_id: "", media_type: "movie", title: "", poster_url: "", year: "" });

  // upload form
  const [up, setUp] = useState({ file: null, version: "dublado", quality: "FHD", season: "", episode: "" });
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const loadTitles = useCallback(() => {
    api.get("/vod/titles").then((r) => setTitles(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { loadTitles(); }, [loadTitles]);

  const doSearch = async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    setTmdbError(null);
    try {
      const { data } = await api.get("/tmdb/search", { params: { q } });
      setResults(data);
    } catch (e) {
      setTmdbError(formatApiError(e));
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const onQueryChange = (v) => {
    setQuery(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => doSearch(v), 500);
  };

  const addTitle = async (payload) => {
    try {
      const { data } = await api.post("/vod/titles", null, { params: payload });
      toast.success(data.deduplicated ? "Título já existia — deduplicado pelo TMDB ID" : `"${data.title.title}" adicionado ao catálogo`);
      setResults([]);
      setQuery("");
      setManual({ tmdb_id: "", media_type: "movie", title: "", poster_url: "", year: "" });
      loadTitles();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteTitle = async (id) => {
    try {
      await api.delete(`/vod/titles/${id}`);
      toast.success("Título removido");
      setSelected(null);
      loadTitles();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doUpload = async () => {
    if (!up.file) { toast.error("Selecione um arquivo de vídeo"); return; }
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    fd.append("file", up.file);
    fd.append("title_id", selected.id);
    fd.append("version", up.version);
    fd.append("quality", up.quality);
    if (selected.media_type === "tv" && up.season) fd.append("season", up.season);
    if (selected.media_type === "tv" && up.episode) fd.append("episode", up.episode);
    try {
      const { data } = await axios.post(`${API}/vod/upload`, fd, {
        withCredentials: true,
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      if (data.status === "upgraded") {
        toast.success(`Upgrade aplicado: ${data.report.old_quality} → ${data.report.new_quality}. Relatório gerado!`);
      } else if (data.awaiting_digital) {
        toast.warning(`Versão ${data.version.quality} marcada para substituição automática quando sair a digital.`);
      } else {
        toast.success(`Versão criada (${data.fragments} fragmento${data.fragments > 1 ? "s" : ""} no Telegram)`);
      }
      setUp({ file: null, version: "dublado", quality: "FHD", season: "", episode: "" });
      loadTitles();
      const { data: fresh } = await api.get("/vod/titles");
      setSelected(fresh.find((t) => t.id === selected.id) || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const filtered = (titles || []).filter((t) => !filter || t.title?.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="catalog-page">
      <PageHeader eyebrow="Motor VOD" title="Catálogo Inteligente" subtitle="Busque no TMDB, adicione títulos e gerencie versões. Dublado e Legendado coexistem — o upgrade de qualidade é automático." />

      <TutorialHint
        id="catalog"
        title="Como funciona a curadoria"
        steps={[
          "Pesquise um filme ou série — os metadados vêm do TMDB automaticamente.",
          "Clique em Gerenciar e faça upload de uma versão (ex: Legendado FHD).",
          "Suba Dublado depois: as duas versões coexistem, nunca se apagam.",
          "Se subir a mesma versão em qualidade superior, o sistema substitui e gera relatório.",
          "Versões CAM/TS ficam na fila de substituição automática.",
        ]}
      />

      <GlassCard className="p-5 anim-fade-up" data-testid="tmdb-search-card">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="tmdb-search-input"
              className="input-dark w-full rounded-xl pl-11 pr-4 py-3 text-sm"
              placeholder="Buscar filme ou série no TMDB..."
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
            />
          </div>
        </div>
        {searching && <div className="flex justify-center py-6"><Spinner /></div>}
        {tmdbError && (
          <div className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs" data-testid="tmdb-error-msg">
            {tmdbError} — use o cadastro manual abaixo.
          </div>
        )}
        {results.length > 0 && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="tmdb-results">
            {results.map((r) => (
              <div key={`${r.media_type}-${r.tmdb_id}`} className="flex gap-3 p-3 rounded-xl bg-slate-950/50 border border-slate-800/60 items-center">
                {r.poster_url ? (
                  <img src={r.poster_url} alt={r.title} className="w-12 h-[72px] rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-[72px] rounded-lg bg-slate-800 flex items-center justify-center"><Film size={18} className="text-slate-600" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{r.title}</p>
                  <p className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                    {r.media_type === "tv" ? <Tv size={11} /> : <Film size={11} />} {r.year} · TMDB {r.tmdb_id}
                  </p>
                </div>
                <button
                  data-testid={`tmdb-add-${r.tmdb_id}`}
                  onClick={() => addTitle({ tmdb_id: r.tmdb_id, media_type: r.media_type })}
                  className="btn-neon p-2 rounded-lg shrink-0"
                  title="Adicionar ao catálogo"
                >
                  <Plus size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
        <details className="mt-4">
          <summary className="text-xs text-cyan-400/80 cursor-pointer hover:text-cyan-300" data-testid="manual-add-toggle">Cadastro manual (sem TMDB)</summary>
          <div className="mt-3 grid sm:grid-cols-5 gap-3">
            <input data-testid="manual-tmdb-id" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="TMDB ID" value={manual.tmdb_id} onChange={(e) => setManual({ ...manual, tmdb_id: e.target.value })} />
            <select data-testid="manual-media-type" className="input-dark rounded-xl px-3 py-2.5 text-sm" value={manual.media_type} onChange={(e) => setManual({ ...manual, media_type: e.target.value })}>
              <option value="movie">Filme</option>
              <option value="tv">Série</option>
            </select>
            <input data-testid="manual-title" className="input-dark rounded-xl px-3 py-2.5 text-sm sm:col-span-2" placeholder="Título" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} />
            <button
              data-testid="manual-add-btn"
              onClick={() => manual.tmdb_id && manual.title && addTitle({ tmdb_id: parseInt(manual.tmdb_id), media_type: manual.media_type, title: manual.title, poster_url: manual.poster_url || null, year: manual.year ? parseInt(manual.year) : null })}
              className="btn-violet rounded-xl px-4 py-2.5 text-sm"
            >
              Adicionar
            </button>
          </div>
        </details>
      </GlassCard>

      <div className="flex items-center gap-3">
        <input
          data-testid="catalog-filter-input"
          className="input-dark rounded-xl px-4 py-2.5 text-sm w-full sm:w-72"
          placeholder="Filtrar catálogo..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <span className="text-xs text-slate-500 font-mono2" data-testid="catalog-count">{filtered.length} títulos</span>
      </div>

      {titles === null ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Clapperboard} title="Catálogo vazio" subtitle="Busque um título no TMDB acima para começar sua curadoria." testid="catalog-empty" />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="catalog-grid">
          {filtered.map((t) => (
            <GlassCard key={t.id} data-testid={`vod-title-card-${t.id}`} className="glass-hover overflow-hidden cursor-pointer" onClick={() => setSelected(t)}>
              {t.poster_url ? (
                <img src={t.poster_url} alt={t.title} className="w-full h-56 object-cover" />
              ) : (
                <div className="w-full h-56 bg-slate-900 flex items-center justify-center"><Film size={28} className="text-slate-700" /></div>
              )}
              <div className="p-3">
                <p className="text-xs font-semibold text-white truncate">{t.title}</p>
                <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                  {t.media_type === "tv" ? <Tv size={10} /> : <Film size={10} />} {t.year || "—"} · {t.versions.length} versões
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {t.versions.slice(0, 3).map((v) => <QualityBadge key={v.id} quality={v.quality} />)}
                  {t.versions.some((v) => v.low_quality) && <AlertTriangle size={12} className="text-rose-400" />}
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.title || ""} testid="title-modal">
        {selected && (
          <div className="space-y-6">
            <div className="flex gap-4">
              {selected.poster_url && <img src={selected.poster_url} alt="" className="w-20 rounded-xl object-cover" />}
              <div className="text-xs text-slate-400 space-y-1">
                <p className="flex items-center gap-1.5">{selected.media_type === "tv" ? <Tv size={12} /> : <Film size={12} />} {selected.media_type === "tv" ? "Série" : "Filme"} · {selected.year || "—"} · TMDB {selected.tmdb_id}</p>
                {selected.overview && <p className="leading-relaxed line-clamp-3">{selected.overview}</p>}
                {user.role === "super_admin" && (
                  <button data-testid="title-delete-btn" onClick={() => deleteTitle(selected.id)} className="inline-flex items-center gap-1.5 text-rose-400 hover:text-rose-300 mt-2">
                    <Trash2 size={12} /> Remover título
                  </button>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-mono2 uppercase tracking-widest text-slate-400 mb-3">Versões ({selected.versions.length})</h4>
              {selected.versions.length === 0 ? (
                <p className="text-xs text-slate-500" data-testid="versions-empty">Nenhuma versão ainda. Faça o primeiro upload abaixo.</p>
              ) : (
                <div className="space-y-2" data-testid="versions-list">
                  {selected.versions.map((v) => (
                    <div key={v.id} className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-slate-950/50 border border-slate-800/60">
                      <VersionBadge version={v.version} testid={`version-badge-${v.id}`} />
                      <QualityBadge quality={v.quality} testid={`quality-badge-${v.id}`} />
                      {v.season != null && <span className="text-[10px] font-mono2 text-slate-400">S{String(v.season).padStart(2, "0")}E{String(v.episode).padStart(2, "0")}</span>}
                      <span className="text-[10px] text-slate-500 ml-auto">{fmtBytes(v.size_bytes)} · {(v.parts?.length || v.telegram_file_ids?.length || 0)} fragmentos</span>
                      {v.low_quality && (
                        <span className="text-[10px] text-rose-400 inline-flex items-center gap-1" data-testid={`low-quality-flag-${v.id}`}>
                          <AlertTriangle size={10} /> aguardando digital
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-slate-800/60 pt-5">
              <h4 className="text-xs font-mono2 uppercase tracking-widest text-slate-400 mb-3">Upload de versão</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <select data-testid="upload-version-select" className="input-dark rounded-xl px-3 py-2.5 text-sm" value={up.version} onChange={(e) => setUp({ ...up, version: e.target.value })}>
                  {VERSIONS.map((v) => <option key={v.v} value={v.v}>{v.label}</option>)}
                </select>
                <select data-testid="upload-quality-select" className="input-dark rounded-xl px-3 py-2.5 text-sm" value={up.quality} onChange={(e) => setUp({ ...up, quality: e.target.value })}>
                  {QUALITIES.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
                {selected.media_type === "tv" && (
                  <>
                    <input data-testid="upload-season-input" type="number" min="1" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="Temporada" value={up.season} onChange={(e) => setUp({ ...up, season: e.target.value })} />
                    <input data-testid="upload-episode-input" type="number" min="1" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="Episódio" value={up.episode} onChange={(e) => setUp({ ...up, episode: e.target.value })} />
                  </>
                )}
                <label className="sm:col-span-2 input-dark rounded-xl px-4 py-3 text-sm cursor-pointer flex items-center gap-3 text-slate-400 hover:border-cyan-400/40 transition-colors" data-testid="upload-file-label">
                  <Upload size={16} className="text-cyan-400 shrink-0" />
                  <span className="truncate">{up.file ? up.file.name : "Escolher arquivo de vídeo..."}</span>
                  <input data-testid="upload-file-input" type="file" accept="video/*,.mkv,.mp4,.avi" className="hidden" onChange={(e) => setUp({ ...up, file: e.target.files[0] })} />
                </label>
              </div>
              {uploading && (
                <div className="mt-3" data-testid="telegram-upload-chunk-progress">
                  <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full upload-progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-[10px] font-mono2 text-cyan-400 mt-1.5">{progress}% — fragmentando e enviando ao Telegram...</p>
                </div>
              )}
              <button data-testid="upload-submit-btn" onClick={doUpload} disabled={uploading} className="btn-neon mt-4 px-6 py-3 rounded-xl text-sm w-full sm:w-auto flex items-center justify-center gap-2">
                {uploading ? <Spinner /> : <><Upload size={15} /> Enviar para o Telegram</>}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
